// frontend/src/modules/admin/components/GestionSecurite.jsx
// Panel sécurité - Analyse bots + fail2ban
// 73+ Papy

import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Bug, 
  Ban, 
  Server, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Loader,
  Clock,
  HardDrive,
  Cpu,
  Activity,
  Database,
  Download,
  Upload,
  Trash2,
  Calendar,
  FileArchive,
  Users,
  LogIn,
  LogOut,
  UserCheck,
  UserX,
  Eye,
  Filter
} from 'lucide-react';
import { useMessageBox } from '../../../components/MessageBox';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionSecurite = () => {
  // Hook MessageBox
  const { showMessage, showConfirm, confirmDelete } = useMessageBox();

  const [botsData, setBotsData] = useState(null);
  const [fail2banData, setFail2banData] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // États pour Gestion Données (Backup/Restore)
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);

  // États pour Logs Connexions
  const [connexions, setConnexions] = useState([]);
  const [connexionStats, setConnexionStats] = useState(null);
  const [loadingConnexions, setLoadingConnexions] = useState(false);
  const [connexionFilter, setConnexionFilter] = useState('today'); // today, week, month
  const [connexionRoleFilter, setConnexionRoleFilter] = useState('all'); // all, client, admin, superviseur

  // Chargement initial
  useEffect(() => {
    loadAllData();
    // Rafraîchissement automatique toutes les 60 secondes
    const interval = setInterval(loadAllData, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadAllData = async () => {
    try {
      const [botsRes, fail2banRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/security/bots`),
        fetch(`${API_BASE}/security/fail2ban`),
        fetch(`${API_BASE}/security/status`)
      ]);

      if (botsRes.ok) {
        const bots = await botsRes.json();
        setBotsData(bots.data);
      }

      if (fail2banRes.ok) {
        const fail2ban = await fail2banRes.json();
        setFail2banData(fail2ban.data);
      }

      if (statusRes.ok) {
        const status = await statusRes.json();
        setStatusData(status.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleUnban = async (ip) => {
    const ok = await showConfirm(`Débannir l'IP ${ip} ?`, 'warning');
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/security/unban/${ip}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage('success', data.message);
        loadAllData();
      } else {
        showMessage('error', data.message);
      }
    } catch (error) {
      showMessage('error', 'Erreur débannissement');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES SAUVEGARDES
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Charger la liste des sauvegardes
  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const res = await fetch(`${API_BASE}/security/backups`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setBackups(data.data || []);
        }
      }
    } catch (error) {
      console.error('Erreur chargement backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  // Lancer une sauvegarde
  const handleBackup = async () => {
    const ok = await showConfirm(
      'Lancer une sauvegarde de la base de données ?\n\nCette opération peut prendre quelques secondes.',
      'question',
      'Nouvelle sauvegarde'
    );
    if (!ok) return;

    setBackupInProgress(true);
    try {
      const res = await fetch(`${API_BASE}/security/backup`, {
        method: 'POST'
      });
      const data = await res.json();

      if (data.success) {
        showMessage('success', `Sauvegarde créée !\n\n📁 ${data.filename}\n📊 Taille : ${data.size}`);
        loadBackups(); // Recharger la liste
      } else {
        showMessage('error', `Erreur : ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur sauvegarde : ${error.message}`);
    } finally {
      setBackupInProgress(false);
    }
  };

  // Restaurer une sauvegarde
  const handleRestore = async (backup) => {
    const ok = await confirmDelete(
      `⚠️ ATTENTION ⚠️\n\nRestaurer la sauvegarde du ${backup.dateFormatted} ?\n\nCette action va ÉCRASER toutes les données actuelles !\n\nFichier : ${backup.filename}`
    );
    if (!ok) return;

    // Double confirmation pour action critique
    const okFinal = await showConfirm(
      'Êtes-vous VRAIMENT sûr ?\n\nToutes les données actuelles seront perdues !',
      'warning',
      'Confirmation finale'
    );
    if (!okFinal) return;

    setRestoreInProgress(true);
    try {
      const res = await fetch(`${API_BASE}/security/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: backup.filename })
      });
      const data = await res.json();

      if (data.success) {
        showMessage('success', `Base de données restaurée avec succès !\n\nRedémarrage recommandé.`);
      } else {
        showMessage('error', `Erreur : ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur restauration : ${error.message}`);
    } finally {
      setRestoreInProgress(false);
    }
  };

  // Supprimer une sauvegarde
  const handleDeleteBackup = async (backup) => {
    const ok = await confirmDelete(`Supprimer la sauvegarde du ${backup.dateFormatted} ?`);
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/security/backup/${backup.filename}`, {
        method: 'DELETE'
      });
      const data = await res.json();

      if (data.success) {
        showMessage('success', 'Sauvegarde supprimée');
        loadBackups();
      } else {
        showMessage('error', `Erreur : ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur suppression : ${error.message}`);
    }
  };

  // Télécharger une sauvegarde
  const handleDownloadBackup = (backup) => {
    window.open(`${API_BASE}/security/backup/download/${backup.filename}`, '_blank');
  };

  // Charger backups quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'data') {
      loadBackups();
    }
    if (activeTab === 'connexions') {
      loadConnexions();
      loadConnexionStats();
    }
  }, [activeTab, connexionFilter]);

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES LOGS DE CONNEXIONS
  // ═══════════════════════════════════════════════════════════════════════════

  const loadConnexions = async () => {
    setLoadingConnexions(true);
    try {
      let url = `${API_BASE}/security/connexions/`;
      if (connexionFilter === 'today') {
        url += 'today';
      } else {
        const jours = connexionFilter === 'week' ? 7 : 30;
        url += `recent?jours=${jours}&limit=200`;
      }

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setConnexions(data.data || []);
        }
      }
    } catch (error) {
      console.error('Erreur chargement connexions:', error);
    } finally {
      setLoadingConnexions(false);
    }
  };

  const loadConnexionStats = async () => {
    try {
      const jours = connexionFilter === 'today' ? 1 : connexionFilter === 'week' ? 7 : 30;
      const res = await fetch(`${API_BASE}/security/connexions/stats?jours=${jours}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setConnexionStats(data.data);
        }
      }
    } catch (error) {
      console.error('Erreur chargement stats connexions:', error);
    }
  };

  // Filtrer les connexions par rôle
  const filteredConnexions = connexions.filter(c => {
    if (connexionRoleFilter === 'all') return true;
    return c.role === connexionRoleFilter;
  });

  // Couleur selon le statut de connexion
  const getConnexionStatusColor = (statut) => {
    switch (statut) {
      case 'succes': return 'bg-green-100 text-green-800';
      case 'echec': return 'bg-red-100 text-red-800';
      case 'deconnexion': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Icône selon le type de connexion
  const getConnexionTypeIcon = (type) => {
    switch (type) {
      case 'email': return '📧';
      case 'qrcode': return '📱';
      case 'magic_link': return '🔗';
      case 'admin_2fa': return '🔐';
      default: return '👤';
    }
  };

  // Badge rôle
  const getRoleBadge = (role) => {
    switch (role) {
      case 'superviseur': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'client': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Couleur selon niveau d'alerte
  const getAlertColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 border-red-500 text-red-800';
      case 'warning': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'elevated': return 'bg-orange-100 border-orange-500 text-orange-800';
      default: return 'bg-green-100 border-green-500 text-green-800';
    }
  };

  const getAlertIcon = (level) => {
    switch (level) {
      case 'critical':
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-600" />
        <span className="ml-3 text-gray-600">Chargement données sécurité...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            🛡️ Sécurité & Surveillance
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Monitoring bots, fail2ban et état serveur
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: '📊 Vue d\'ensemble', icon: Activity },
            { id: 'bots', label: '🐛 Bots', icon: Bug },
            { id: 'fail2ban', label: '⚔️ Fail2ban', icon: Ban },
            { id: 'server', label: '🖥️ Serveur', icon: Server },
            { id: 'connexions', label: '👥 Connexions', icon: Users },
            { id: 'data', label: '💾 Données', icon: Database }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET VUE D'ENSEMBLE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card Bots */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Bug className="w-5 h-5 text-yellow-600" />
              Bots détectés
            </h3>
            {botsData && (
              <>
                <div className={`p-3 rounded-lg border-l-4 mb-4 ${getAlertColor(botsData.alertLevel)}`}>
                  <div className="flex items-center gap-2">
                    {getAlertIcon(botsData.alertLevel)}
                    <span className="font-medium">{botsData.alertMessage}</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total requêtes bots :</span>
                    <span className="font-bold text-yellow-600">{botsData.totalBots}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Python/aiohttp :</span>
                    <span>{botsData.python?.count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Go-http-client :</span>
                    <span>{botsData.go?.count || 0}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Card Fail2ban */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />
              Fail2ban
            </h3>
            {fail2banData && (
              <>
                <div className={`p-3 rounded-lg border-l-4 mb-4 ${getAlertColor(fail2banData.alertLevel)}`}>
                  <div className="flex items-center gap-2">
                    {getAlertIcon(fail2banData.alertLevel)}
                    <span className="font-medium">{fail2banData.alertMessage}</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Status :</span>
                    <span className={fail2banData.active ? 'text-green-600 font-bold' : 'text-red-600'}>
                      {fail2banData.active ? '✅ Actif' : '❌ Inactif'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bans aujourd'hui :</span>
                    <span className="font-bold text-red-600">{fail2banData.todayStats?.bans || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IPs bannies :</span>
                    <span>{fail2banData.currentlyBanned || 0}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Card Serveur */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-600" />
              Serveur
            </h3>
            {statusData && (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Uptime :
                  </span>
                  <span className="text-green-600">{statusData.server?.uptime || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">
                    <Cpu className="w-4 h-4" /> Charge :
                  </span>
                  <span>{statusData.server?.load?.['1min'] || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">
                    <HardDrive className="w-4 h-4" /> RAM :
                  </span>
                  <span className={statusData.server?.memory?.percent > 80 ? 'text-red-600' : ''}>
                    {statusData.server?.memory?.percent || 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Disque :</span>
                  <span className={statusData.server?.disk?.percent > 80 ? 'text-red-600' : ''}>
                    {statusData.server?.disk?.percent || 0}%
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <span>nginx :</span>
                    <span className={statusData.services?.nginx === 'active' ? 'text-green-600' : 'text-red-600'}>
                      {statusData.services?.nginx === 'active' ? '✅' : '❌'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>mysql :</span>
                    <span className={statusData.services?.mysql === 'active' ? 'text-green-600' : 'text-red-600'}>
                      {statusData.services?.mysql === 'active' ? '✅' : '❌'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Farm-Booking :</span>
                    <span className={statusData.services?.farmBooking === 'online' ? 'text-green-600' : 'text-red-600'}>
                      {statusData.services?.farmBooking === 'online' ? '✅' : '❌'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET BOTS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'bots' && botsData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top IPs */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🌐 Top 10 IPs bots</h3>
            {botsData.topIps?.length > 0 ? (
              <div className="space-y-2">
                {botsData.topIps.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-mono text-sm">{item.ip}</span>
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">
                      {item.count} req
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Aucune IP bot détectée</p>
            )}
          </div>

          {/* Top URLs */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🎯 Top 10 URLs ciblées</h3>
            {botsData.topUrls?.length > 0 ? (
              <div className="space-y-2">
                {botsData.topUrls.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-mono text-xs truncate max-w-[200px]" title={item.url}>
                      {item.url}
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Aucune URL ciblée</p>
            )}
          </div>

          {/* Fichiers sensibles */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">⚠️ Fichiers sensibles recherchés</h3>
            {botsData.sensitiveFiles?.length > 0 ? (
              <div className="space-y-2">
                {botsData.sensitiveFiles.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-mono text-xs text-red-700 truncate max-w-[200px]">
                      {item.file}
                    </span>
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-green-600 text-sm">✅ Aucun fichier sensible ciblé</p>
            )}
          </div>

          {/* Activité horaire */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">⏰ Activité aujourd'hui (par heure)</h3>
            {botsData.hourlyActivity?.length > 0 ? (
              <div className="grid grid-cols-6 gap-2">
                {botsData.hourlyActivity.map((item, idx) => (
                  <div key={idx} className="text-center p-2 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">{item.hour}h</div>
                    <div className="font-bold text-sm">{item.count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Pas d'activité aujourd'hui</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET FAIL2BAN */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'fail2ban' && fail2banData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Jails */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🔒 Jails actifs</h3>
            {fail2banData.jails?.length > 0 ? (
              <div className="space-y-3">
                {fail2banData.jails.map((jail, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900">{jail.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Total bans : {jail.totalBanned} | Actuels : {jail.currentlyBanned}
                    </div>
                    {jail.bannedIps?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {jail.bannedIps.map((ip, ipIdx) => (
                          <span 
                            key={ipIdx} 
                            className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-mono cursor-pointer hover:bg-red-200"
                            onClick={() => handleUnban(ip)}
                            title="Cliquer pour débannir"
                          >
                            {ip} ×
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Aucun jail configuré</p>
            )}
          </div>

          {/* Stats du jour */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">📈 Stats du jour</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">{fail2banData.todayStats?.bans || 0}</div>
                <div className="text-sm text-gray-600">Bans</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{fail2banData.todayStats?.unbans || 0}</div>
                <div className="text-sm text-gray-600">Unbans</div>
              </div>
            </div>
          </div>

          {/* Derniers bans */}
          <div className="bg-white shadow rounded-lg p-6 lg:col-span-2">
            <h3 className="text-lg font-medium text-gray-900 mb-4">⚔️ 10 derniers bans</h3>
            {fail2banData.recentBans?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Jail</th>
                      <th className="px-4 py-2 text-left">IP</th>
                      <th className="px-4 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fail2banData.recentBans.map((ban, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-2 text-gray-600">{ban.date}</td>
                        <td className="px-4 py-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                            {ban.jail}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono">{ban.ip}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleUnban(ban.ip)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            Débannir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Aucun ban récent</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET SERVEUR */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'server' && statusData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ressources */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">💻 Ressources système</h3>
            <div className="space-y-4">
              {/* RAM */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>RAM</span>
                  <span>{statusData.server?.memory?.used || 0} / {statusData.server?.memory?.total || 0} MB</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${statusData.server?.memory?.percent > 80 ? 'bg-red-600' : 'bg-green-600'}`}
                    style={{ width: `${statusData.server?.memory?.percent || 0}%` }}
                  ></div>
                </div>
              </div>

              {/* Disque */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Disque</span>
                  <span>{statusData.server?.disk?.used || '0'} / {statusData.server?.disk?.total || '0'}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${statusData.server?.disk?.percent > 80 ? 'bg-red-600' : 'bg-blue-600'}`}
                    style={{ width: `${statusData.server?.disk?.percent || 0}%` }}
                  ></div>
                </div>
              </div>

              {/* Load */}
              <div className="pt-4">
                <div className="text-sm font-medium mb-2">Charge système</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-gray-50 rounded">
                    <div className="text-lg font-bold">{statusData.server?.load?.['1min'] || 0}</div>
                    <div className="text-xs text-gray-500">1 min</div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <div className="text-lg font-bold">{statusData.server?.load?.['5min'] || 0}</div>
                    <div className="text-xs text-gray-500">5 min</div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <div className="text-lg font-bold">{statusData.server?.load?.['15min'] || 0}</div>
                    <div className="text-xs text-gray-500">15 min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🔧 Services</h3>
            <div className="space-y-3">
              {Object.entries(statusData.services || {}).map(([service, status]) => (
                <div key={service} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium capitalize">{service}</span>
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    status === 'active' || status === 'online' || status.includes('process')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {status}
                  </span>
                </div>
              ))}
            </div>

            {/* Uptime */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-600">Uptime serveur</div>
              <div className="text-lg font-bold text-blue-700">
                {statusData.server?.uptime || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET CONNEXIONS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'connexions' && (
        <div className="space-y-6">
          {/* Stats rapides */}
          {connexionStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{connexionStats.global?.total || 0}</div>
                <div className="text-sm text-gray-500">Total connexions</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{connexionStats.global?.succes || 0}</div>
                <div className="text-sm text-gray-500">Succès</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-600">{connexionStats.global?.echecs || 0}</div>
                <div className="text-sm text-gray-500">Échecs</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">{connexionStats.global?.users_uniques || 0}</div>
                <div className="text-sm text-gray-500">Utilisateurs</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-orange-600">{connexionStats.global?.ips_uniques || 0}</div>
                <div className="text-sm text-gray-500">IPs uniques</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-indigo-600">
                  {connexionStats.global?.duree_moyenne 
                    ? Math.round(connexionStats.global.duree_moyenne / 60) + 'm' 
                    : '-'}
                </div>
                <div className="text-sm text-gray-500">Durée moy.</div>
              </div>
            </div>
          )}

          {/* Filtres */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Période :</span>
                <select
                  value={connexionFilter}
                  onChange={(e) => setConnexionFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="today">Aujourd'hui</option>
                  <option value="week">7 derniers jours</option>
                  <option value="month">30 derniers jours</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Rôle :</span>
                <select
                  value={connexionRoleFilter}
                  onChange={(e) => setConnexionRoleFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="all">Tous</option>
                  <option value="client">Clients</option>
                  <option value="admin">Admins</option>
                  <option value="superviseur">Superviseurs</option>
                </select>
              </div>

              <button
                onClick={() => { loadConnexions(); loadConnexionStats(); }}
                disabled={loadingConnexions}
                className="flex items-center gap-2 px-4 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loadingConnexions ? 'animate-spin' : ''}`} />
                Actualiser
              </button>

              <div className="ml-auto text-sm text-gray-500">
                {filteredConnexions.length} connexion(s)
              </div>
            </div>
          </div>

          {/* Stats par rôle */}
          {connexionStats?.parRole && connexionStats.parRole.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {connexionStats.parRole.map((stat, idx) => (
                <div key={idx} className="bg-white shadow rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadge(stat.role)}`}>
                      {stat.role === 'superviseur' ? '👑 Superviseur' : 
                       stat.role === 'admin' ? '⚙️ Admin' : '👤 Client'}
                    </span>
                    <span className="text-2xl font-bold">{stat.nb}</span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-green-600">✓ {stat.succes} succès</span>
                    <span className="text-red-600">✗ {stat.echecs} échecs</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tableau des connexions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <LogIn className="w-5 h-5 text-blue-600" />
              Journal des connexions
            </h3>

            {loadingConnexions ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-gray-500" />
                <span className="ml-2 text-gray-500">Chargement...</span>
              </div>
            ) : filteredConnexions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune connexion pour cette période</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Date/Heure</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Utilisateur</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Rôle</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">IP</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Statut</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredConnexions.map((conn, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium">{conn.date_jour || 'Aujourd\'hui'}</div>
                          <div className="text-xs text-gray-500">{conn.heure}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{conn.nom_prenom || '-'}</div>
                          <div className="text-xs text-gray-500">{conn.email || conn.code_client}</div>
                        </td>
                        <td className="px-3 py-2">
                          {conn.role && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadge(conn.role)}`}>
                              {conn.role}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span title={conn.type_connexion}>
                            {getConnexionTypeIcon(conn.type_connexion)}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{conn.ip_address || '-'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getConnexionStatusColor(conn.statut)}`}>
                            {conn.statut === 'succes' ? '✓ OK' : 
                             conn.statut === 'echec' ? '✗ Échec' : '⏹ Déco'}
                          </span>
                          {conn.motif_echec && (
                            <div className="text-xs text-red-500 mt-1">{conn.motif_echec}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {conn.duree_affichee || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Échecs de connexion (alerte sécurité) */}
          {connexionStats?.echecs && connexionStats.echecs.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6 border-l-4 border-red-500">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Tentatives échouées
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="px-3 py-2 text-left font-medium text-red-700">IP</th>
                      <th className="px-3 py-2 text-left font-medium text-red-700">Email tenté</th>
                      <th className="px-3 py-2 text-left font-medium text-red-700">Motif</th>
                      <th className="px-3 py-2 text-left font-medium text-red-700">Nb échecs</th>
                      <th className="px-3 py-2 text-left font-medium text-red-700">Dernier</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {connexionStats.echecs.map((echec, idx) => (
                      <tr key={idx} className="hover:bg-red-50">
                        <td className="px-3 py-2 font-mono">{echec.ip_address || '-'}</td>
                        <td className="px-3 py-2">{echec.email || '-'}</td>
                        <td className="px-3 py-2 text-red-600">{echec.motif_echec || '-'}</td>
                        <td className="px-3 py-2">
                          <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold">
                            {echec.nb_echecs}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{echec.dernier_echec}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top utilisateurs actifs */}
          {connexionStats?.topUsers && connexionStats.topUsers.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-green-600" />
                Top 10 utilisateurs actifs
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {connexionStats.topUsers.map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                      <div>
                        <div className="font-medium">{user.nom_prenom || user.email}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600">{user.nb_connexions}</div>
                      <div className="text-xs text-gray-500">connexions</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET DONNÉES (BACKUP/RESTORE) */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'data' && (
        <div className="space-y-6">
          {/* Actions rapides */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Gestion des données
            </h3>
            
            <div className="flex flex-wrap gap-4">
              {/* Bouton Sauvegarde */}
              <button
                onClick={handleBackup}
                disabled={backupInProgress}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {backupInProgress ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                {backupInProgress ? 'Sauvegarde en cours...' : 'Nouvelle sauvegarde'}
              </button>

              {/* Bouton Rafraîchir liste */}
              <button
                onClick={loadBackups}
                disabled={loadingBackups}
                className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${loadingBackups ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              💡 Les sauvegardes incluent toutes les tables de la base de données Farm-Booking.
            </p>
          </div>

          {/* Tableau des sauvegardes */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-purple-600" />
              Sauvegardes disponibles
              <span className="ml-2 bg-purple-100 text-purple-700 text-sm px-2 py-0.5 rounded-full">
                {backups.length}
              </span>
            </h3>

            {loadingBackups ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-gray-500" />
                <span className="ml-2 text-gray-500">Chargement...</span>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune sauvegarde disponible</p>
                <p className="text-sm mt-1">Cliquez sur "Nouvelle sauvegarde" pour en créer une</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">📅 Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">📁 Fichier</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">📊 Taille</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {backups.map((backup, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{backup.dateFormatted}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{backup.timeFormatted}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-gray-600">{backup.filename}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
                            {backup.sizeFormatted}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {/* Bouton Restaurer */}
                            <button
                              onClick={() => handleRestore(backup)}
                              disabled={restoreInProgress}
                              className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50 text-sm transition-colors"
                              title="Restaurer cette sauvegarde"
                            >
                              <Upload className="w-4 h-4" />
                              Restaurer
                            </button>

                            {/* Bouton Télécharger */}
                            <button
                              onClick={() => handleDownloadBackup(backup)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm transition-colors"
                              title="Télécharger le fichier"
                            >
                              <Download className="w-4 h-4" />
                            </button>

                            {/* Bouton Supprimer */}
                            <button
                              onClick={() => handleDeleteBackup(backup)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm transition-colors"
                              title="Supprimer cette sauvegarde"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Avertissement */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">⚠️ Attention</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    La restauration d'une sauvegarde <strong>écrase toutes les données actuelles</strong>. 
                    Assurez-vous de faire une sauvegarde avant toute restauration !
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Infos stockage */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-gray-600" />
              Informations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Emplacement</div>
                <div className="font-mono text-gray-700 mt-1">/opt/farm-booking/backups/</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Format</div>
                <div className="font-medium text-gray-700 mt-1">SQL compressé (.sql.gz)</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Rétention suggérée</div>
                <div className="font-medium text-gray-700 mt-1">7 dernières sauvegardes</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer avec timestamp */}
      <div className="text-center text-sm text-gray-500">
        Dernière mise à jour : {new Date().toLocaleString('fr-FR')}
        <span className="ml-2">• Rafraîchissement auto : 60s</span>
      </div>
    </div>
  );
};

export default GestionSecurite;